import { HTTPResponseError, type HTTPService } from "@soft-db/http";
import type { BatchRecipientsUpdate, Recipients } from "./recipients";
import type { Coordinates, PaginatedResponse } from "./shared";

export type Station = {
	id: string;
	legacyId: string;
	serialNumber: string;
	site: ObjectSummary | null;
	space: string | null;
	readonly type: number;
	distributor: string;
	// TODO: Delete when api change
	organization: string;
	status: StationStatus;
	name: string;
	description: string;
	statusDataType: StatusDataType;
	coordinates: Coordinates;
	staging: boolean;
	autoUpdateCoordinates: boolean;
	inFavorite: boolean;
	isSubscribed: boolean;
	sensors: Sensor[];
	recipients: Recipients;
};

export type ObjectSummary = {
	id: string;
	name: string;
};

export type SensorType = "sound" | "vibration";

export type Sensor = {
	id: string;
	serialNumber: string;
	activeOnStation: boolean;
	name: string;
	type: SensorType;
};

export enum StatusDataType {
	SLM,
	ACC,
	VEL,
	OPL,
}

export type StationStatusState = "offline" | "online" | "waiting";

export type StationStatus = {
	time: string;
	dt: number;
	state: StationStatusState;
	sound: number[];
	accelerationPeak: number[];
	velocityPeak: number[];
	overpressure: number[];
	powerLevel: number;
	isCharging: boolean;
	coordinates: Coordinates;
	type: StatusDataType;
	station: string;
};

export type StationPartialStatus = {
	state: StationStatusState;
	time: string;
};

export type StationConfigAuthor =
	| {
			type: "usb";
			pcName: string;
	  }
	| {
			type: "web";
			user: string;
	  };

export type HardwareConfig = {
	id: string;
	serialNumber: string;
	author: StationConfigAuthor;
	createdAt: string;
	firmware: number;
	hardware: number;
	config: string;
};

export type CreateStation = {
	serialNumber: string;
	name: string;
	distributor: string;
	rate: 1 | 2 | 3;
};

export type UpdateStation = {
	name?: string;
	description?: string;
	coordinates?: Coordinates;
	autoUpdateCoordinates?: boolean;
	statusDataType?: number;
	serialNumber?: string;
	staging?: boolean;
	recipients?: BatchRecipientsUpdate;
};

export type HardwareConfigContext =
	| {
			type: "station";
			station: string;
	  }
	| {
			type: "site";
			site: string;
	  };

export type HardwareConfigSearchFilter = {
	startTime?: Date;
	endTime?: Date;
	pageSize?: number;
	pageIndex?: number;
};

export class StationApi {
	constructor(private _http: HTTPService) {}

	async fetchContext() {
		return await this._http.get<Station[]>("stations");
	}

	/**
	 * !Only possible for sys_admin user.
	 */
	async fetchAll() {
		return await this._http.get<Station[]>("stations", {
			params: { all: true },
		});
	}

	async fetchFavorites() {
		return await this._http.get<Station[]>("stations/favorites");
	}

	async fetch(idOrSn: string) {
		return await this._http.get<Station>(`stations/${idOrSn}`);
	}

	async fetchDist(id: string) {
		return await this._http.get<Station[]>(`distributors/${id}/stations`);
	}

	async fetchSpace(id: string) {
		return await this._http.get<Station[]>(`spaces/${id}/stations`);
	}

	async serialNumberIsFree(sn: string): Promise<boolean> {
		try {
			await this._http.head(`stations/${sn}`);
			return false;
		} catch (e) {
			if (e instanceof HTTPResponseError && e.status === 404) {
				return true;
			}

			throw e;
		}
	}

	async create(station: CreateStation) {
		return await this._http.post<Station>("stations", station);
	}

	async update(id: string, update: UpdateStation) {
		return await this._http.patch<Station>(`stations/${id}`, update);
	}

	async delete(id: string) {
		return await this._http.delete(`stations/${id}`);
	}

	// TODO: Params & return type
	async fetchLogs(id: string) {
		return await this._http.get(`stations/${id}/logs`);
	}

	async linkSite(id: string, site: string) {
		return await this._http.put<Station>(`stations/${id}/site`, { site });
	}

	async unlinkSite(id: string) {
		return await this._http.delete<Station>(`stations/${id}/site`);
	}

	async getLatestHarwdareConfig(context: HardwareConfigContext) {
		const url = `${this.#hardwareConfigBaseUrl(context)}/hardware-configs/latest`;
		return await this._http.get<HardwareConfig>(url);
	}

	async searchHardwareConfig(
		context: HardwareConfigContext,
		filter: HardwareConfigSearchFilter = {}
	) {
		const url = `${this.#hardwareConfigBaseUrl(context)}/hardware-configs`;

		return await this._http.get<PaginatedResponse<HardwareConfig>>(url, {
			params: filter,
		});
	}

	#hardwareConfigBaseUrl(context: HardwareConfigContext) {
		if (context.type === "station") {
			return `stations/${context.station}`;
		} else {
			return `sites/${context.site}`;
		}
	}

	async transfer(id: string, distributor: string | null) {
		return await this._http.post<Station>(`stations/${id}/transfer`, {
			distributor,
		});
	}
}
